// components/CustomDropdown.tsx
'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import { useState, useRef, useEffect } from 'react';

interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  subtext?: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  label?: string;
  showSearch?: boolean;
  position?: 'left' | 'right' | 'center';
  hideDisabled?: boolean;
  type?: string;
  modalLanguage?: any;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  className = '',
  label,
  showSearch = false,
  position = 'right',
  hideDisabled = false,
  type,
  modalLanguage
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {locale} = useRouter();

  const selectedOption = options.find(option => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter options - remove disabled ones if hideDisabled is true
  const filteredOptions = showSearch
    ? options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (!hideDisabled || !option.disabled)
      )
    : options.filter(option => !hideDisabled || !option.disabled);

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -10, scale: 0.95 }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'left':
        return 'left-0';
      case 'center':
        return 'left-1/2 transform -translate-x-1/2';
      case 'right':
      default:
        return 'right-0';
    }
  };

  // Function to get flag URL
  const getFlagUrl = (countryCode: string) => {
    return `https://flagsapi.com/${countryCode}/shiny/24.png`;
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className={`block text-sm font-medium text-gray-700 mb-1 ${modalLanguage === 'ar' ? 'text-right' : 'text-left'}`}>
          {label}
        </label>
      )}
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full ${modalLanguage === 'ar' ? 'flex' : 'flex flex-row-reverse'} items-center justify-between px-4 py-2.5
          border border-gray-300 rounded-lg
          bg-white text-gray-700
          hover:bg-gray-50 hover:border-gray-400
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          transition-all duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
          relative z-20
        `}
      >
        <div className={`items-center gap-2 w-full ${modalLanguage === 'ar' ? 'text-right flex': 'text-left flex flex-row-reverse'}`}>
          {/* Show flag for country dropdown type */}
          {type === 'country' && selectedOption && (
            <img
              src={getFlagUrl(selectedOption.value)}
              alt={selectedOption.value}
              className="w-5 h-5 object-contain"
            />
          )}
          {type === 'currency' && selectedOption?.icon}
          <span className="truncate">
            {selectedOption?.label || placeholder}
          </span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop to prevent clicks behind */}
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={dropdownVariants}
              transition={{ duration: 0.15 }}
              className={`
                fixed z-50
                ${getPositionClasses()}
                bg-white rounded-lg shadow-lg
                border border-gray-200
                max-h-60 overflow-y-auto
                min-w-[200px]
              `}
              style={{
                // Position the dropdown relative to the viewport
                top: dropdownRef.current ? 
                dropdownRef.current.getBoundingClientRect().bottom + 4 : 
                'auto',
                [position === 'right' ? 'right' : 'left']: dropdownRef.current ?
                  (position === 'right' ? 
                    window.innerWidth - dropdownRef.current.getBoundingClientRect().right :
                    dropdownRef.current.getBoundingClientRect().left) + 'px' :
                  'auto',
                width: dropdownRef.current ? dropdownRef.current.offsetWidth + 'px' : 'auto'
              }}
            >
              {showSearch && (
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}

              <div className="py-1">
                {filteredOptions.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-500 text-center">
                    No options available
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        onChange(option.value);
                        setIsOpen(false);
                        setSearchTerm('');
                      }}
                      className={`
                        w-full flex items-center justify-between px-4 py-2.5
                        text-left text-sm
                        transition-colors duration-150
                        ${value === option.value
                          ? 'bg-blue-50 text-blue-700'
                          : 'hover:bg-gray-50 text-gray-700'
                        }
                        cursor-pointer
                      `}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {/* Show flag for country dropdown type */}
                        {type === 'country' && (
                          <img
                            src={getFlagUrl(option.value)}
                            alt={option.value}
                            className="w-5 h-5 object-contain flex-shrink-0"
                          />
                        )}
                        {type === 'currency' && option.icon}
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          {/* {option.subtext && (
                            <span className="text-xs text-gray-500 mt-0.5">{option.subtext}</span>
                          )} */}
                        </div>
                      </div>
                      {value === option.value && (
                        <svg
                          className="w-4 h-4 text-blue-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDropdown;